import { useRef, useState } from 'react';
import { Modal } from '@/shared/components/Modal';
import { AnswerSheet } from '@/modules/gabarito/presentation/components/AnswerSheet';
import { setAnswer, type Answers } from '../../domain/entities/answer.entity';

interface EnsaioAnswersModalProps {
  open: boolean;
  totalQuestions: number;
  optionCount: 4 | 5;
  /** O que já está respondido: a folha abre com isso marcado. */
  initialAnswers: Answers;
  onClose: () => void;
  /** As respostas da folha inteira — substituem as do ensaio. */
  onConfirm: (answers: Answers) => void;
}

/**
 * "Fornecer respostas": a folha do gabarito, servindo ao ensaio. É para quem
 * baixou a prova e resolveu no papel — marcar trinta respostas pela folha (com
 * o teclado, inclusive) é bem mais rápido que abrir questão por questão.
 *
 * **A folha é um rascunho até confirmar**: o que se marca aqui fica no estado
 * deste diálogo, e fechar sem confirmar não muda o ensaio. Confirmado, as
 * respostas substituem as do ensaio, e a pessoa confere uma a uma na tela de
 * questões antes de finalizar.
 */
export function EnsaioAnswersModal({
  open,
  totalQuestions,
  optionCount,
  initialAnswers,
  onClose,
  onConfirm,
}: EnsaioAnswersModalProps) {
  if (!open) return null;

  return (
    <SheetDialog
      totalQuestions={totalQuestions}
      optionCount={optionCount}
      initialAnswers={initialAnswers}
      onClose={onClose}
      onConfirm={onConfirm}
    />
  );
}

/**
 * Separado para o estado da folha nascer a cada abertura a partir das
 * respostas atuais — e não ficar com o que sobrou de uma abertura cancelada.
 */
function SheetDialog({
  totalQuestions,
  optionCount,
  initialAnswers,
  onClose,
  onConfirm,
}: Omit<EnsaioAnswersModalProps, 'open'>) {
  const [answers, setAnswers] = useState<Answers>(initialAnswers);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const answeredCount = Object.keys(answers).length;

  return (
    <Modal
      open
      size="wide"
      onClose={onClose}
      title="Fornecer respostas do ensaio"
      note={
        <>
          Marque o que você respondeu na prova baixada. Ao confirmar, as
          respostas preenchem o ensaio e você confere questão por questão antes
          de finalizar.
          <span className="kbd-hint">
            {' '}
            Use as teclas <kbd>A</kbd>–
            <kbd>{optionCount === 5 ? 'E' : 'D'}</kbd> para marcar e{' '}
            <kbd>↑</kbd> <kbd>↓</kbd> para navegar.
          </span>
        </>
      }
    >
      <div className="sheet-scroll">
        <AnswerSheet
          totalQuestions={totalQuestions}
          optionCount={optionCount}
          answers={answers}
          onAnswer={(question, answer) =>
            setAnswers((current) => setAnswer(current, question, answer))
          }
          submitRef={confirmRef}
        />
      </div>

      <div className="modal-actions sheet-actions">
        <button type="button" className="ghost ghost-lg" onClick={onClose}>
          Cancelar
        </button>
        <button
          ref={confirmRef}
          type="button"
          className="cta cta-inline cta-block"
          onClick={() => onConfirm(answers)}
        >
          Confirmar {answeredCount}{' '}
          {answeredCount === 1 ? 'resposta' : 'respostas'}
        </button>
      </div>
    </Modal>
  );
}
